import { useEffect, useRef } from 'react';
import { Icono } from './Icono';
import type { NombreIcono } from './iconos';

/**
 * RV Deshacer (componentes.md §18), **solo la pieza visual**: botón negro con la línea blanca que se vacía en 5 s y, con
 * `texto`, el aviso «Hecho · Deshacer». El envío retrasado de 5 s que describe el diseño NO se hace aquí: espera a que el
 * equipo confirme P0-10; hasta entonces la pantalla que lo use decide qué hace `onDeshacer` y `onFin`.
 */
interface Props {
    texto?: string;
    onDeshacer: () => void;
    onFin?: () => void;
    duracion?: number;
    /** Si cambia, el tiempo vuelve a empezar. */
    clave?: string | number;
    icono?: NombreIcono;
    fondo?: 'tarjeta' | 'suave';
    movil?: boolean;
    ancho?: 'auto' | '100%';
    grande?: boolean;
    etiqueta?: string;
    /** Idioma del botón por defecto (G928, como RV Fecha): `en`, «Undo». */
    idioma?: 'es' | 'en';
}

export function Deshacer({ texto, onDeshacer, onFin, duracion = 5000, clave, icono = 'check', fondo = 'tarjeta', movil = false, ancho = 'auto', grande = false, idioma = 'es', etiqueta = idioma === 'en' ? 'Undo' : 'Deshacer' }: Props) {
    const fin = useRef(onFin);
    useEffect(() => {
        fin.current = onFin;
    });
    useEffect(() => {
        const t = setTimeout(() => fin.current?.(), duracion);
        return () => {
            clearTimeout(t);
        };
    }, [clave, duracion]);

    const alto = grande ? 56 : movil ? 48 : 44;
    const boton = (extra: string, flex?: string) => (
        <button
            type="button"
            className={`rv-deshacer-b ${extra}`}
            style={{ minHeight: alto, fontSize: grande ? 16 : 14, fontWeight: grande ? 700 : 600, width: flex === undefined ? ancho : undefined, flex }}
            onClick={onDeshacer}
        >
            <Icono nombre="undo-2" />
            <span>{etiqueta}</span>
            <span key={String(clave ?? '')} aria-hidden="true" className="rv-deshacer-b__barra" style={{ animation: `rvVacia ${String(duracion)}ms linear forwards` }} />
        </button>
    );

    if (texto === undefined) {
        return boton('rv-deshacer-a');
    }
    const suave = fondo === 'suave';
    return (
        <div
            className="rv-deshacer-a"
            role="status"
            style={{ width: '100%', boxSizing: 'border-box', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 14px', padding: '12px 12px 12px 16px', borderRadius: 12, background: suave ? 'var(--rv-bone)' : '#fff', boxShadow: suave ? 'none' : 'var(--rv-shadow-2)', color: 'var(--rv-ink)' }}
        >
            <span style={{ width: 32, height: 32, borderRadius: 10, background: suave ? '#fff' : 'var(--rv-paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icono nombre={icono} tamano={16} />
            </span>
            <span style={{ flex: '1 1 200px', minWidth: 0, fontSize: 14, lineHeight: 1.45 }}>{texto}</span>
            {boton('', movil ? '1 1 100%' : '0 0 auto')}
        </div>
    );
}
